import { X } from 'lucide-react';
import { useCallback, useId } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from './hooks/useFocusTrap';
import styles from './Modal.module.css';

export interface ModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly maxWidth?: string;
  /** Blocks backdrop-click dismissal — use while a submit is in flight. */
  readonly dismissible?: boolean;
  /**
   * Drops the titled header bar and the body padding so the caller owns the
   * whole canvas — used by ConfirmDialog for its centred icon-led layout. The
   * dialog semantics are unchanged: `title` still labels it for assistive tech,
   * it is just not painted as a header row.
   */
  readonly bare?: boolean;
}

/**
 * A real dialog: role, aria-modal, focus trap, Escape, focus restore, portalled.
 *
 * The prototype put the dismiss handler on the backdrop <div> (unreachable by keyboard)
 * and trapped nothing. Here Escape is handled by useFocusTrap and the backdrop closes
 * on mousedown of the backdrop itself, so a drag that ends outside does not dismiss.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = '520px',
  dismissible = true,
  bare = false,
}: ModalProps) {
  const titleId = useId();
  const subtitleId = useId();
  const containerRef = useFocusTrap(isOpen, onClose, dismissible);

  const onBackdropMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!dismissible) return;
      if (event.target === event.currentTarget) onClose();
    },
    [dismissible, onClose],
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={onBackdropMouseDown}
      role="presentation"
    >
      <div
        ref={containerRef}
        className={styles.content}
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        {...(bare
          ? // Bare dialogs draw their own heading, so pointing at a hidden copy
            // would put the title in the DOM twice. The name comes from the
            // attribute instead.
            { 'aria-label': title }
          : { 'aria-labelledby': titleId })}
        aria-describedby={subtitle === undefined ? undefined : subtitleId}
        tabIndex={-1}
      >
        {bare ? (
          <>
            <button
              type="button"
              className={`${styles.close} ${styles.closeFloating}`}
              onClick={onClose}
              aria-label="Close dialog"
            >
              <X size={18} aria-hidden="true" />
            </button>
            {children}
          </>
        ) : (
          <>
            <div className={styles.header}>
              <div>
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
                {subtitle !== undefined && (
                  <p id={subtitleId} className={styles.subtitle}>
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close dialog"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className={styles.body}>{children}</div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
